import React, { useEffect, useRef, useState } from 'react';
import { bool, arrayOf } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { useLocation, useHistory } from 'react-router-dom';

import { propTypes } from '../../util/types';
import {
  Page,
  LayoutSingleColumn,
  ItemSourceToggle,
  BrandCarousel,
  IconClose,
} from '../../components';
import { BRANDS, brandLabel } from '../../config/brandRegistry';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import CatalogSearchContent from '../../components/CatalogSearchContent/CatalogSearchContent';
import KeywordAutocompleteInput from '../../components/KeywordAutocompleteInput/KeywordAutocompleteInput';

import { setDOMLoaded, setFirstLoaded } from '../../ducks/ui.duck';
import { fetchStoresWithReviews } from '../../ducks/user.duck';
import { listingMatchesCondition } from '../../util/listingCondition';
import { listingMatchesDeliveryOptions } from '../../util/listingDeliveryOptions';
import { parseSelectFilterOptions } from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import { PUBLIC_CATALOG_STOCK_FILTER } from '../../util/publicCatalogStock';
import {
  isWarehouseChannel,
  isWarehouseListing,
  WAREHOUSE_SOURCE_QUERY_VALUE,
} from '../../util/itemSourceChannel';
import {
  STORE_SIDEBAR_CATEGORIES,
  getCategoryLabel,
  getSubcategoryLabel,
} from '../../util/storeTaxonomy';
import { sanitizeTaxonomyFilterParams } from '../SearchPage/SearchPage.shared';
import { useIframeDetection } from '../../hooks/useIframeDetection';

// Import new sections
import SectionHeroCustom from './sections/SectionHeroCustom';
import SectionFeaturedListings from './sections/SectionFeaturedListings';
import SectionFeaturedDealers from './sections/SectionFeaturedDealers';
import StoreSidebar from '../../components/StoreSidebar/StoreSidebar';
import SearchPagination from './sections/SearchPagination';

import css from './LandingPageCustom.module.css';
import SectionInfo from './sections/SectionInfo';
import {
  fetchStaffPicks as fetchStaffPicksApi,
  interpretSearchV2,
  suggestSearchV2,
} from '../../util/api';
import {
  AI_ATTRIBUTION_PARAM,
  AI_QUERY_PARAM,
  INTERPRET_MAX_QUERY_CHARS,
  exactSearchParams,
  interpretedSearchParams,
  normInterpretText,
  shouldInterpretQuery,
  withAiAttribution,
} from '../../util/searchV2Interpret';
import { filterListingsByBrand, getAvailableBrandValues } from '../../util/brandAvailability';
import { buildSearchV2Request } from './searchV2Adapter';
import { useSearchV2Pages } from './searchV2Pages';

const SERVICES_LISTING_TYPE = 'services';
const PRODUCTS_CATALOG_MODE = 'products';
export const STAFF_PICKS_ITEMS_PER_PAGE = 20;
const SERVICES_CATALOG_MODE = 'services';
// Browser deadline of POST /api/search/interpret (the server's own timeout is 1500 ms).
const INTERPRET_DEADLINE_MS = 2000;

const getCatalogModeForPath = pathname =>
  pathname === '/services' ? SERVICES_CATALOG_MODE : PRODUCTS_CATALOG_MODE;

const isServiceListing = listing =>
  listing?.attributes?.publicData?.listingType === SERVICES_LISTING_TYPE;

const filterListingsByCatalogMode = (listings, catalogMode) => {
  if (!Array.isArray(listings)) {
    return [];
  }

  return catalogMode === SERVICES_CATALOG_MODE
    ? listings.filter(isServiceListing)
    : listings.filter(listing => !isServiceListing(listing));
};

export const sanitizeCatalogParams = (params, catalogMode) => {
  const sanitizedTaxonomyParams = sanitizeTaxonomyFilterParams(params || {});

  if (catalogMode === SERVICES_CATALOG_MODE) {
    const allowedKeys = new Set(['keywords', 'price', 'iframe']);
    return Object.fromEntries(
      Object.entries(sanitizedTaxonomyParams).filter(
        ([key, value]) => allowedKeys.has(key) && value != null && value !== ''
      )
    );
  }

  const { pub_listingType, ...rest } = sanitizedTaxonomyParams;
  // Only 'warehouse' is a valid non-default Item Source channel. Anything else
  // (including the 'in-store' default) is dropped so the URL stays canonical and
  // self-correcting, consistent with how the other filter params are sanitized.
  if (rest.channel !== 'warehouse') {
    delete rest.channel;
  }
  return rest;
};

export const getStaffPicksExitPath = params => {
  const sanitizedParams = sanitizeCatalogParams(params, PRODUCTS_CATALOG_MODE);
  const queryString = stringify(sanitizedParams);

  return queryString ? `/s?${queryString}` : '/s';
};

export const paginateListings = (listings, page, pageSize = STAFF_PICKS_ITEMS_PER_PAGE) => {
  const safeListings = Array.isArray(listings) ? listings : [];
  const totalPages = Math.max(1, Math.ceil(safeListings.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  return {
    currentPage: safePage,
    totalPages,
    listings: safeListings.slice((safePage - 1) * pageSize, safePage * pageSize),
  };
};

export const LandingPageCustomComponent = props => {
  const { currentUser, stores, domLoaded, intl } = props;
  const location = useLocation();
  const history = useHistory();
  const isIframe = useIframeDetection();

  // /s is the products search page, /services is the services search page, / is the landing page
  const urlParams = parse(location.search);
  const catalogMode = getCatalogModeForPath(location.pathname);
  const isStaffPicksMode = location.pathname === '/staff-picks';
  const isServicesMode = catalogMode === SERVICES_CATALOG_MODE;
  const sanitizedUrlParams = sanitizeCatalogParams(urlParams, catalogMode);
  const isSearchMode =
    location.pathname === '/s' || location.pathname === '/services' || isStaffPicksMode;
  const currentSearchPath = isStaffPicksMode ? '/staff-picks' : isServicesMode ? '/services' : '/s';
  // `/s` reads from Search V2; `/services`, Staff Picks and the homepage keep their own loaders.
  const isProductSearch = location.pathname === '/s';

  const [listings, setListings] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [availableBrandValues, setAvailableBrandValues] = useState(null);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);
  const [topSearchValue, setTopSearchValue] = useState(urlParams.keywords || '');
  const [sortValue, setSortValue] = useState('newest');
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 768
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [staffPicksGeneratedAt, setStaffPicksGeneratedAt] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // `/s` AI query understanding: at most one interpretation in flight; a newer submit, suggestion,
  // Clear All or navigation obsoletes it (request id + abort).
  const [interpreting, setInterpreting] = useState(false);
  const interpretRequest = useRef({ id: 0, controller: null, timer: null });
  // Draft protection: every direct edit of the search box bumps `inputRevision`. An interpretation
  // that completes after the text was edited still navigates, but `keepDraftForSearch` (the
  // `location.search` of that one navigation) stops the URL → input sync from replacing the draft.
  const inputRevision = useRef(0);
  const keepDraftForSearch = useRef(null);
  const searchV2 = useSearchV2Pages(
    buildSearchV2Request(sanitizedUrlParams, sortValue),
    isProductSearch
  );

  useEffect(() => {
    let isCancelled = false;
    props.handleDomLoaded();

    if (isStaffPicksMode) {
      setSearchResultsLoading(true);
      Promise.resolve(
        props.onFetchStaffPicks(
          sanitizedUrlParams,
          setSearchResults,
          setAvailableBrandValues,
          setStaffPicksGeneratedAt
        )
      ).finally(() => {
        if (!isCancelled) setSearchResultsLoading(false);
      });
    } else if (isProductSearch) {
      // Loaded by useSearchV2Pages.
    } else if (isSearchMode) {
      setSearchResultsLoading(true);
      Promise.resolve(
        props.onFetchSearchResults(
          sanitizedUrlParams,
          setSearchResults,
          setAvailableBrandValues,
          sortValue,
          catalogMode
        )
      ).finally(() => {
        if (!isCancelled) {
          setSearchResultsLoading(false);
        }
      });
    } else {
      props.onFetchHomepageStaffPicks(setListings, setStaffPicksGeneratedAt);
    }

    return () => {
      isCancelled = true;
    };
  }, [catalogMode, isStaffPicksMode, location.search, sortValue]);

  useEffect(() => {
    if (!isSearchMode) {
      return;
    }

    const rawQueryString = stringify(urlParams);
    const sanitizedQueryString = stringify(sanitizedUrlParams);

    if (rawQueryString !== sanitizedQueryString) {
      history.replace(
        sanitizedQueryString ? `${currentSearchPath}?${sanitizedQueryString}` : currentSearchPath
      );
    }
  }, [currentSearchPath, history, isSearchMode, location.search]);

  useEffect(() => {
    if (keepDraftForSearch.current === location.search) return;
    setTopSearchValue(urlParams.keywords || '');
  }, [urlParams.keywords]);
  const handleSearchInputChange = value => {
    inputRevision.current += 1;
    setTopSearchValue(value);
  };

  const cancelInterpretation = () => {
    const request = interpretRequest.current;
    request.id += 1;
    if (request.controller) request.controller.abort();
    clearTimeout(request.timer);
    request.controller = null;
    request.timer = null;
  };
  // Any navigation is newer than a pending interpretation; unmounting aborts it.
  useEffect(() => {
    if (interpretRequest.current.controller) {
      cancelInterpretation();
      setInterpreting(false);
    }
    // one-shot: back/forward and every later navigation sync the input as before
    keepDraftForSearch.current = null;
  }, [location.search]);
  useEffect(() => cancelInterpretation, []);

  // Reset to page 1 whenever any filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortValue, location.search]);

  useEffect(() => {
    if (domLoaded && !props.firstLoaded) {
      props.handleFirstLoaded();
      props.handleStores();
    }
  }, [listings]);

  const schemaTitle = intl.formatMessage(
    { id: 'LandingPageCustom.schemaTitle' },
    { marketplaceName: 'BowTackle' }
  );
  const schemaDescription = intl.formatMessage({ id: 'LandingPageCustom.schemaDescription' });
  const activeCategoryLabel = isServicesMode
    ? null
    : getCategoryLabel(sanitizedUrlParams.pub_category);
  const activeSubcategoryLabel = isServicesMode
    ? null
    : getSubcategoryLabel(sanitizedUrlParams.pub_subcategory);

  const activeBreadcrumbs = [];
  if (!isServicesMode && sanitizedUrlParams.pub_category) {
    activeBreadcrumbs.push(activeCategoryLabel || sanitizedUrlParams.pub_category);
  }
  if (!isServicesMode && sanitizedUrlParams.pub_subcategory) {
    activeBreadcrumbs.push(
      activeSubcategoryLabel || sanitizedUrlParams.pub_subcategory.replace(/-/g, ' ')
    );
  }
  if (sanitizedUrlParams.keywords) activeBreadcrumbs.push(`"${sanitizedUrlParams.keywords}"`);
  if (!isServicesMode && sanitizedUrlParams.pub_brand)
    activeBreadcrumbs.push(brandLabel(sanitizedUrlParams.pub_brand));
  if (sanitizedUrlParams.price) {
    activeBreadcrumbs.push(`$${sanitizedUrlParams.price.replace(',', ' – $')}`);
  }
  const activeCategory = isServicesMode ? '' : sanitizedUrlParams.pub_category || '';
  const searchTabTitle = isStaffPicksMode
    ? 'Staff Picks | BowTackle'
    : sanitizedUrlParams.keywords
    ? `${isServicesMode ? 'Services' : 'Products'} results for "${
        sanitizedUrlParams.keywords
      }" | BowTackle`
    : activeCategory
    ? `${activeCategoryLabel || activeCategory} | BowTackle`
    : isServicesMode
    ? 'Browse Services | BowTackle'
    : 'Browse Products | BowTackle';

  const pageTitle = isSearchMode ? searchTabTitle : schemaTitle;

  // Always show the same landing page, but use search results when available.
  // Brand / deliveryOption / price are filtered server-side in fetchSearchResults.
  const displayListings = isSearchMode ? searchResults : listings;

  // `/s`: Search V2 cursor pages (visited pages plus at most one next page), shown as returned.
  // Other modes: client-side pagination of the complete result set.
  const searchV2LastPage = searchV2.pages[searchV2.pages.length - 1];
  const pagination = isProductSearch
    ? {
        currentPage: searchV2.pageIndex + 1,
        totalPages: searchV2.pages.length + (searchV2LastPage?.nextCursor ? 1 : 0),
        listings: searchV2.pages[searchV2.pageIndex]?.listings || [],
      }
    : paginateListings(displayListings, currentPage);
  const totalPages = pagination.totalPages;
  const safePage = pagination.currentPage;
  const pagedListings = pagination.listings;
  const handlePageChange = isProductSearch ? page => searchV2.goToPage(page - 1) : setCurrentPage;
  const resultsLoading = isProductSearch ? searchV2.loading || interpreting : searchResultsLoading;
  const brandValues = isProductSearch ? searchV2.brandValues : availableBrandValues;
  const searchUnavailable = isProductSearch && !!searchV2.error;
  const searchUnavailableMessage = 'Search is temporarily unavailable.';
  const retryButton = (
    <button type="button" className={css.searchRetryButton} onClick={searchV2.retry}>
      Retry
    </button>
  );
  const iframeCurrentCategory = isServicesMode ? 'all' : sanitizedUrlParams.pub_category || 'all';
  const iframeCurrentCategoryLabel = isServicesMode
    ? 'All Services'
    : getCategoryLabel(iframeCurrentCategory) ||
      (iframeCurrentCategory === 'all' ? 'All Products' : iframeCurrentCategory);
  const iframeCurrentSubcategoryLabel =
    !isServicesMode && sanitizedUrlParams.pub_subcategory
      ? activeSubcategoryLabel || sanitizedUrlParams.pub_subcategory.replace(/-/g, ' ')
      : null;

  const handleIframeCategoryClick = selectedCategory => {
    if (isServicesMode) {
      return;
    }

    const newParams = { ...sanitizedUrlParams };
    if (selectedCategory === 'all') {
      delete newParams.pub_category;
      delete newParams.pub_subcategory;
    } else {
      newParams.pub_category = selectedCategory;
      delete newParams.pub_subcategory;
    }

    const cleaned = sanitizeCatalogParams(
      Object.fromEntries(Object.entries(newParams).filter(([_, v]) => v != null && v !== '')),
      catalogMode
    );
    const qs = stringify(cleaned);
    history.push(qs ? `${currentSearchPath}?${qs}` : currentSearchPath);
  };

  const pushToUrl = newParams => {
    const merged = { ...sanitizedUrlParams, ...newParams };
    const cleaned = sanitizeCatalogParams(
      Object.fromEntries(Object.entries(merged).filter(([_, v]) => v != null && v !== '')),
      catalogMode
    );
    const qs = stringify(cleaned);
    const currentQueryString = stringify(sanitizedUrlParams);
    if (qs === currentQueryString) {
      return;
    }
    history.push(qs ? `${currentSearchPath}?${qs}` : currentSearchPath);
  };

  const currentChannel = sanitizedUrlParams.channel === 'warehouse' ? 'warehouse' : 'in-store';
  const handleChannelChange = next => {
    // Default 'in-store' clears the param to keep the URL clean.
    pushToUrl({ channel: next === 'warehouse' ? 'warehouse' : null });
  };

  const pushKeywords = (keywordValue, otherParams) => {
    if (keywordValue) {
      history.push(`${currentSearchPath}?${stringify({ ...otherParams, keywords: keywordValue })}`);
    } else {
      const qs = stringify(otherParams);
      history.push(qs ? `${currentSearchPath}?${qs}` : currentSearchPath);
    }
  };

  // Gated manual `/s` submit (docs/search-v2-s-autocomplete-ai.md, P3D): no search runs until the
  // interpretation resolves; then exactly one navigation — the interpreted URL, or the typed text
  // on any failure, `fallback` or the 2 s deadline. Sort is never changed. Text typed while it was
  // pending stays in the box.
  const interpretThenSearch = (text, otherParams) => {
    const request = interpretRequest.current;
    const id = request.id;
    const submittedRevision = inputRevision.current;
    const controller = new AbortController();
    request.controller = controller;
    request.timer = setTimeout(() => controller.abort(), INTERPRET_DEADLINE_MS);
    setInterpreting(true);
    interpretSearchV2({ query: normInterpretText(text) }, { signal: controller.signal })
      .catch(() => null)
      .then(response => {
        if (request.id !== id) return;
        clearTimeout(request.timer);
        request.controller = null;
        request.timer = null;
        setInterpreting(false);
        const interpretation =
          response && response.status === 'interpreted' ? response.interpretation : null;
        const params = interpretation
          ? withAiAttribution(
              otherParams,
              sanitizeCatalogParams(
                interpretedSearchParams(otherParams, interpretation, text),
                catalogMode
              )
            )
          : { ...otherParams, ...(text ? { keywords: text } : {}) };
        const qs = stringify(params);
        if (inputRevision.current === submittedRevision) {
          setTopSearchValue(params.keywords || '');
        } else {
          keepDraftForSearch.current = qs ? `?${qs}` : '';
        }
        history.push(qs ? `${currentSearchPath}?${qs}` : currentSearchPath);
      });
  };

  // `next`: keywords committed by a suggestion; anything else (no argument, a click or submit
  // event) submits the search box as typed. A keyword submit ends a previous AI interpretation
  // (`ai_q` and `ai_a` removed).
  const handleTopSearch = next => {
    cancelInterpretation();
    setInterpreting(false);
    const {
      keywords,
      [AI_QUERY_PARAM]: aiQuery,
      [AI_ATTRIBUTION_PARAM]: aiAttribution,
      ...otherParams
    } = sanitizedUrlParams;
    const keywordValue = typeof next === 'string' ? next : topSearchValue;
    if (isProductSearch && typeof next !== 'string' && shouldInterpretQuery(keywordValue)) {
      interpretThenSearch(keywordValue, otherParams);
      return;
    }
    pushKeywords(keywordValue, otherParams);
  };

  // `/s` autocomplete (docs/search-v2-s-autocomplete-ai.md): suggestions for the typed text within
  // the current channel and filters; selecting one is a single URL update.
  const fetchKeywordSuggestions = (query, options) => {
    const { context, filters } = buildSearchV2Request(sanitizedUrlParams, sortValue);
    return suggestSearchV2({ query, context, filters }, options).then(r => r.suggestions);
  };
  const handleSuggestionSelect = suggestion => {
    const { keywords, filters } = suggestion.action || {};
    if (filters) {
      cancelInterpretation();
      setInterpreting(false);
      setTopSearchValue('');
      pushToUrl({
        keywords: null,
        [AI_QUERY_PARAM]: null,
        [AI_ATTRIBUTION_PARAM]: null,
        ...(filters.brand ? { pub_brand: filters.brand } : {}),
        ...(filters.category
          ? { pub_category: filters.category, pub_subcategory: filters.subcategory || null }
          : {}),
      });
    } else if (typeof keywords === 'string') {
      // A chosen title asks for that product: Relevance keeps it from landing past page 1 under
      // Newest/Price. Identifier and model/SKU suggestions keep the current sort. Batched with the
      // navigation (event handler), so the search runs once, with Relevance.
      if (suggestion.type === 'title') setSortValue('relevance');
      setTopSearchValue(keywords);
      handleTopSearch(keywords);
    }
  };

  const handleClearFilters = () => {
    cancelInterpretation();
    setInterpreting(false);
    setSortValue('newest');
    setCurrentPage(1);
    history.push(
      sanitizedUrlParams.iframe ? `${currentSearchPath}?iframe=true` : currentSearchPath
    );
  };

  // `ai_q` / `ai_a`: the original text of an interpreted search and the filters it changed
  // (survive reload/share; never sent to /api/search). "Search exactly" searches that text and
  // undoes only the filters `ai_a` attributes to the interpretation, without AI.
  const aiQuery =
    isProductSearch && typeof sanitizedUrlParams[AI_QUERY_PARAM] === 'string'
      ? sanitizedUrlParams[AI_QUERY_PARAM].slice(0, INTERPRET_MAX_QUERY_CHARS)
      : '';
  const handleSearchExactly = () => {
    cancelInterpretation();
    setInterpreting(false);
    const params = sanitizeCatalogParams(exactSearchParams(sanitizedUrlParams), catalogMode);
    setTopSearchValue(params.keywords || '');
    const qs = stringify(params);
    history.push(qs ? `${currentSearchPath}?${qs}` : currentSearchPath);
  };
  const aiNotice = aiQuery ? (
    <div className={css.aiNotice} role="status">
      <span className={css.aiNoticeText}>"{aiQuery}" was refined with filters.</span>
      <button type="button" className={css.aiNoticeButton} onClick={handleSearchExactly}>
        Search exactly for this text
      </button>
    </div>
  ) : null;

  const handleExitStaffPicks = () => {
    setCurrentPage(1);
    history.push(getStaffPicksExitPath(sanitizedUrlParams));
  };

  const iframeSortOptions = [
    { key: '', label: 'Sort By' },
    { key: 'newest', label: 'Newest' },
    { key: 'price-low', label: 'Price: Low to High' },
    { key: 'price-high', label: 'Price: High to Low' },
    { key: 'relevance', label: 'Relevance' },
  ];

  return (
    <Page
      className={css.root}
      scrollingDisabled={false}
      description={schemaDescription}
      title={pageTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: pageTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={
          isSearchMode && isIframe ? (
            <TopbarContainer
              iframeCategories={isServicesMode ? [] : STORE_SIDEBAR_CATEGORIES}
              iframeCurrentCategory={iframeCurrentCategory}
              onIframeCategoryClick={handleIframeCategoryClick}
              iframeSearchKeyword={topSearchValue}
              setIframeSearchKeyword={handleSearchInputChange}
              onIframeSearchSubmit={handleTopSearch}
              iframeSearchSuggestions={
                isProductSearch
                  ? { fetchSuggestions: fetchKeywordSuggestions, onSelect: handleSuggestionSelect }
                  : null
              }
              iframeCatalogLabel={isServicesMode ? 'All Services' : 'All Products'}
              iframeSearchTitle={isServicesMode ? 'Search Services' : 'Search Products'}
              iframeSearchPlaceholder={
                isServicesMode
                  ? 'Tell us more about the service you need...'
                  : "Tell us more about what you're looking for..."
              }
              iframeSearchPath={currentSearchPath}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          ) : (
            <TopbarContainer />
          )
        }
        footer={isSearchMode && isIframe ? null : <FooterContainer />}
      >
        {isSearchMode ? (
          // Search Results Layout
          isIframe ? (
            <>
              {aiNotice}
              <CatalogSearchContent
                categories={isServicesMode ? [] : STORE_SIDEBAR_CATEGORIES}
                currentCategory={iframeCurrentCategory}
                currentCategoryLabel={iframeCurrentCategoryLabel}
                currentSort={isStaffPicksMode ? 'staff-picks' : sortValue}
                currentSubcategoryLabel={iframeCurrentSubcategoryLabel}
                fetchingListings={resultsLoading}
                intl={intl}
                listings={searchUnavailable ? [] : pagedListings}
                emptyMessage={searchUnavailable ? searchUnavailableMessage : undefined}
                mobileMenuOpen={mobileMenuOpen}
                onCategoryClick={handleIframeCategoryClick}
                onParamChange={pushToUrl}
                onResetAll={handleClearFilters}
                onSortChange={e => !isStaffPicksMode && setSortValue(e.target.value)}
                catalogLabel={isServicesMode ? 'All Services' : 'All Products'}
                catalogMode={catalogMode}
                showCategoryNavigation={!isServicesMode}
                showSourceToggle={!isServicesMode}
                resultsFooter={
                  searchUnavailable ? (
                    retryButton
                  ) : (
                    <SearchPagination
                      currentPage={safePage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )
                }
                setMobileMenuOpen={setMobileMenuOpen}
                sortOptions={
                  isStaffPicksMode
                    ? [
                        {
                          key: 'staff-picks',
                          label: intl.formatMessage({ id: 'StaffPicks.order' }),
                        },
                      ]
                    : iframeSortOptions
                }
                urlQueryParams={sanitizedUrlParams}
                brands={isServicesMode ? null : BRANDS}
                availableBrandValues={isServicesMode ? null : brandValues}
                onBrandSelect={name => {
                  setCurrentPage(1);
                  pushToUrl({ pub_brand: name });
                }}
              />
            </>
          ) : (
            <>
              {/* Mobile filter modal — shown only on mobile */}
              {sidebarOpen && (
                <div className={css.mobileFilterOverlay} onClick={() => setSidebarOpen(false)}>
                  <div className={css.mobileFilterPanel} onClick={e => e.stopPropagation()}>
                    <div className={css.mobileFilterHeader}>
                      <span className={css.mobileFilterTitle}>
                        {isStaffPicksMode ? 'Filters' : 'Filter & Sort'}
                      </span>
                      <button
                        className={css.mobileFilterClose}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          stroke="#1E1E1E"
                          strokeWidth="2"
                          strokeLinecap="round"
                        >
                          <line x1="1" y1="1" x2="13" y2="13" />
                          <line x1="13" y1="1" x2="1" y2="13" />
                        </svg>
                      </button>
                    </div>
                    <div className={css.mobileFilterContent}>
                      <StoreSidebar
                        urlQueryParams={sanitizedUrlParams}
                        onParamChange={pushToUrl}
                        onResetAll={handleClearFilters}
                        intl={intl}
                        isMobile
                        catalogMode={catalogMode}
                      />
                    </div>
                    <div className={css.mobileFilterFooter}>
                      <button
                        className={css.mobileFilterApply}
                        onClick={() => setSidebarOpen(false)}
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Top search bar */}
              <div className={css.topSearchBar}>
                <span className={css.topSearchTitle}>
                  {isServicesMode ? 'Search Services' : 'Search Products'}
                </span>
                {isProductSearch ? (
                  <KeywordAutocompleteInput
                    id="s-keyword-search"
                    className={css.topSearchInputWrap}
                    inputClassName={css.topSearchInput}
                    value={topSearchValue}
                    onChange={handleSearchInputChange}
                    onEnter={handleTopSearch}
                    onSelect={handleSuggestionSelect}
                    fetchSuggestions={fetchKeywordSuggestions}
                    placeholder="Tell us more about what you're looking for..."
                    ariaLabel="Search products"
                  />
                ) : (
                  <input
                    className={css.topSearchInput}
                    type="text"
                    value={topSearchValue}
                    onChange={e => handleSearchInputChange(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleTopSearch()}
                    placeholder={
                      isServicesMode
                        ? 'Tell us more about the service you need...'
                        : "Tell us more about what you're looking for..."
                    }
                  />
                )}
                <button className={css.topSearchGoButton} onClick={handleTopSearch}>
                  Go
                </button>
              </div>

              {aiNotice}

              {/* Active filter breadcrumb */}
              {activeBreadcrumbs.length > 0 && (
                <div className={css.filterBreadcrumb}>
                  {activeBreadcrumbs.map((label, i) => (
                    <span key={i} className={css.breadcrumbItem}>
                      {label.toUpperCase()}
                      <span className={css.breadcrumbSep}>›</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Sidebar + content layout (mirrors StorePage) */}
              <div className={css.searchLayout}>
                {sidebarOpen && (
                  <div className={css.desktopSidebar}>
                    <StoreSidebar
                      urlQueryParams={sanitizedUrlParams}
                      onParamChange={pushToUrl}
                      onResetAll={handleClearFilters}
                      intl={intl}
                      catalogMode={catalogMode}
                    />
                  </div>
                )}
                <div className={css.searchMainContent}>
                  <div className={css.searchControlsStack}>
                    {/* Header row: title (left) + Filter & Sort button (right) */}
                    <div className={css.searchHeaderRow}>
                      <h2 className={css.searchTitle}>
                        {isStaffPicksMode
                          ? intl.formatMessage({ id: 'StaffPicks.title' })
                          : sanitizedUrlParams.keywords ||
                            sanitizedUrlParams.pub_category ||
                            sanitizedUrlParams.pub_subcategory ||
                            sanitizedUrlParams.pub_brand
                          ? 'Search results'
                          : isServicesMode
                          ? 'All Services'
                          : 'All Products'}
                      </h2>
                      <div className={css.searchHeaderActions}>
                        {isStaffPicksMode ? (
                          <div className={css.staffPicksOrder}>
                            <span className={css.staffPicksOrderLabel}>
                              {intl.formatMessage({ id: 'StaffPicks.order' })}
                            </span>
                            <button
                              type="button"
                              className={css.staffPicksExit}
                              onClick={handleExitStaffPicks}
                              aria-label={intl.formatMessage({ id: 'StaffPicks.exit' })}
                              title={intl.formatMessage({ id: 'StaffPicks.exit' })}
                            >
                              <IconClose size="small" rootClassName={css.staffPicksExitIcon} />
                            </button>
                          </div>
                        ) : (
                          <select
                            className={css.sortDropdown}
                            value={sortValue}
                            onChange={e => setSortValue(e.target.value)}
                          >
                            <option value="newest">Newest</option>
                            <option value="price-low">Price: Low to High</option>
                            <option value="price-high">Price: High to Low</option>
                            <option value="relevance">Relevance</option>
                          </select>
                        )}
                        <button
                          className={
                            sidebarOpen
                              ? `${css.filterSortButton} ${css.filterSortButtonActive}`
                              : css.filterSortButton
                          }
                          onClick={() => setSidebarOpen(prev => !prev)}
                        >
                          {sidebarOpen
                            ? 'Close filters'
                            : isStaffPicksMode
                            ? 'Filters'
                            : 'Filter & Sort'}
                        </button>
                      </div>
                    </div>
                    {!isServicesMode && (
                      <ItemSourceToggle
                        className={css.itemSourceToggle}
                        value={currentChannel}
                        onChange={handleChannelChange}
                      />
                    )}
                    {!isServicesMode && (
                      <BrandCarousel
                        className={css.brandCarousel}
                        title="Shop by brand"
                        activeBrandName={sanitizedUrlParams.pub_brand}
                        availableBrandValues={brandValues}
                        onSelectBrand={name => {
                          setCurrentPage(1);
                          pushToUrl({ pub_brand: name });
                        }}
                      />
                    )}
                  </div>
                  {isStaffPicksMode && staffPicksGeneratedAt && (
                    <div className={css.staffPicksUpdated}>
                      {intl.formatMessage(
                        { id: 'StaffPicks.updated' },
                        { date: new Date(staffPicksGeneratedAt).toLocaleDateString() }
                      )}
                    </div>
                  )}
                  {searchUnavailable ? (
                    <div className={css.searchStatus}>
                      <p>{searchUnavailableMessage}</p>
                      {retryButton}
                    </div>
                  ) : isProductSearch && resultsLoading && pagedListings.length === 0 ? (
                    <div className={css.searchStatus}>
                      <p>Fetching listings...</p>
                    </div>
                  ) : (
                    <>
                      <SectionFeaturedListings
                        listings={pagedListings}
                        isSearchMode={true}
                        emptyLabelId={
                          isStaffPicksMode
                            ? 'StaffPicks.noListings'
                            : 'SectionFeaturedListings.noListings'
                        }
                      />
                      <SearchPagination
                        currentPage={safePage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </>
                  )}
                </div>
              </div>
            </>
          )
        ) : (
          // Landing Page Layout
          <>
            {/* Hero Section */}
            <SectionHeroCustom currentUser={currentUser} />

            {/* Daily Staff Picks Section */}
            <SectionFeaturedListings
              listings={displayListings}
              title={intl.formatMessage({ id: 'StaffPicks.title' })}
              seeAllRouteName="StaffPicksPage"
              seeAllLabelId="StaffPicks.browseAll"
              emptyLabelId="StaffPicks.noListings"
              isSearchMode={false}
            />

            <SectionInfo />

            {/* Featured Dealers */}
            <SectionFeaturedDealers stores={stores} />

            {/* Featured Innovators - Hidden pending definition */}
            {/* <SectionFeaturedInnovators stores={stores} /> */}
          </>
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageCustomComponent.propTypes = {
  currentUser: propTypes.currentUser,
  listings: arrayOf(propTypes.listing),
  stores: arrayOf(propTypes.currentUser),
  domLoaded: bool,
  firstLoaded: bool,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, stores } = state.user;
  const { domLoaded, firstLoaded } = state.ui;

  return {
    currentUser,
    stores,
    domLoaded,
    firstLoaded,
  };
};

const filterListingsByDeliveryOption = (listings, deliveryFilterRaw) => {
  if (!deliveryFilterRaw) {
    return listings;
  }

  const selectedOptions = parseSelectFilterOptions(String(deliveryFilterRaw));
  if (!selectedOptions.length) {
    return listings;
  }

  return (listings || []).filter(listing => {
    const publicData = listing?.attributes?.publicData || {};
    return listingMatchesDeliveryOptions(publicData, selectedOptions);
  });
};

// Item Source channel filter. 'warehouse' keeps warehouse-sourced listings (WRI
// + BHC); anything else (default 'in-store') keeps the rest (manual + ePro). The
// rule is fully derivable from publicData.source, so absence of source = in-store.
export const filterListingsByChannel = (listings, channelRaw) => {
  const isWarehouse = isWarehouseChannel(channelRaw);
  return (listings || []).filter(listing => {
    const listingIsWarehouse = isWarehouseListing(listing);
    return isWarehouse ? listingIsWarehouse : !listingIsWarehouse;
  });
};

const filterListingsByCondition = (listings, conditionFilterRaw) => {
  if (!conditionFilterRaw) {
    return listings;
  }

  const selectedConditions = parseSelectFilterOptions(String(conditionFilterRaw));
  if (!selectedConditions.length) {
    return listings;
  }

  return (listings || []).filter(listing => {
    const publicData = listing?.attributes?.publicData || {};
    return listingMatchesCondition(publicData, selectedConditions);
  });
};

export const fetchSearchResults = (
  searchParams,
  setSearchResults,
  setAvailableBrandValues,
  sortValue,
  catalogMode = PRODUCTS_CATALOG_MODE
) => (dispatch, getState, sdk) => {
  const searchQuery = {
    page: 1,
    perPage: 100,
    states: ['published'],
    include: ['author', 'author.profileImage', 'images'],
    ...PUBLIC_CATALOG_STOCK_FILTER,
    'fields.listing': [
      'title',
      'createdAt',
      'geolocation',
      'price',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.isAssociated',
      'publicData.creatorId',
      'publicData.creatorType',
      'publicData.deliveryPreference',
      'publicData.shippingAddress',
      'publicData.location',
      'publicData.storedAddress',
      'publicData.category',
      'publicData.subcategory',
      'publicData.videos',
      'publicData.isManufacturer',
      'publicData.hidden',
      'publicData.batchUpload',
      'publicData.isTestListing',
      'publicData.importData',
      'publicData.importSource',
      'publicData.condition',
      'publicData.brand',
      'publicData.deliveryOption',
      'publicData.source',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.isFeatured',
      'publicData.dealerName',
      'publicData.dealerLocation',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.profileImage',
      'profile.publicData',
    ],
    'fields.image': [
      'variants.landscape-crop',
      'variants.listing-card',
      'variants.scaled-medium',
      'variants.square-small',
    ],
    'limit.images': 1,
  };

  // Add search parameters from URL
  if (searchParams.keywords) searchQuery.keywords = searchParams.keywords;
  if (searchParams.pub_category) searchQuery.pub_category = searchParams.pub_category;
  if (searchParams.pub_subcategory) searchQuery.pub_subcategory = searchParams.pub_subcategory;
  if (catalogMode === SERVICES_CATALOG_MODE) {
    searchQuery.pub_listingType = SERVICES_LISTING_TYPE;
  }
  if (searchParams.pub_condition) searchQuery.pub_condition = searchParams.pub_condition;
  if (searchParams.pub_deliveryOption)
    searchQuery.pub_deliveryOption = searchParams.pub_deliveryOption;

  // Item Source channel: Warehouse = warehouse-sourced listings (WRI + BHC).
  // The pub_source parameter is inert today because the field has no Sharetribe
  // search schema, so both branches are finalized client-side over the fully
  // fetched result set, consistent with the condition/deliveryOption fallbacks below.
  if (isWarehouseChannel(searchParams.channel)) {
    searchQuery.pub_source = WAREHOUSE_SOURCE_QUERY_VALUE;
  }

  // Price: URL carries dollars (e.g. "10,500"), Sharetribe's native price filter expects cents
  if (searchParams.price) {
    const [minDollars, maxDollars] = String(searchParams.price)
      .split(',')
      .map(Number);
    if (!Number.isNaN(minDollars) && !Number.isNaN(maxDollars)) {
      searchQuery.price = `${minDollars * 100},${maxDollars * 100}`;
    }
  }

  // Add sort
  if (sortValue === 'newest') {
    searchQuery.sort = '-createdAt';
  } else if (sortValue === 'price-low') {
    searchQuery.sort = 'price,-createdAt';
  } else if (sortValue === 'price-high') {
    searchQuery.sort = '-price,-createdAt';
  } else if (sortValue === 'relevance') {
    searchQuery.sort = 'pub_rank,-createdAt';
  } else {
    searchQuery.sort = '-createdAt';
  }

  const processListings = (allData, allIncluded) => {
    const includedUsers = allIncluded.filter(item => item.type === 'user');
    const beforeFilter = allData.length;
    const approved = allData.filter(listing => {
      const authorId = listing.relationships?.author?.data?.id?.uuid;
      const author = includedUsers.find(user => user.id.uuid === authorId);
      return author?.attributes?.profile?.publicData?.approved_seller === true;
    });

    return approved.map(listing => {
      const authorId = listing.relationships?.author?.data?.id?.uuid;
      const author = allIncluded.find(item => item.type === 'user' && item.id.uuid === authorId);

      let authorWithProfileImage = author;
      if (author && author.relationships?.profileImage?.data?.id?.uuid) {
        const profileImageId = author.relationships.profileImage.data.id.uuid;
        const profileImage = allIncluded.find(
          item => item.type === 'image' && item.id.uuid === profileImageId
        );
        if (profileImage) {
          authorWithProfileImage = { ...author, profileImage };
        }
      }

      const imageIds = listing.relationships?.images?.data?.map(img => img.id.uuid) || [];
      const images = allIncluded.filter(
        item => item.type === 'image' && imageIds.includes(item.id.uuid)
      );

      return { ...listing, author: authorWithProfileImage, images };
    });
  };

  // Fetch page 1 first to get totalPages from meta
  return sdk.listings
    .query(searchQuery)
    .then(response => {
      if (!response.data || !response.data.data) return response;

      const totalPages = response.data.meta?.totalPages || 1;

      // Fetch all remaining pages in parallel
      const remainingPages = [];
      for (let page = 2; page <= totalPages; page++) {
        remainingPages.push(sdk.listings.query({ ...searchQuery, page }));
      }

      return Promise.all(remainingPages).then(restResponses => {
        let allData = [...response.data.data];
        let allIncluded = [...(response.data.included || [])];

        restResponses.forEach(resp => {
          if (resp.data && resp.data.data) {
            allData = allData.concat(resp.data.data);
            allIncluded = allIncluded.concat(resp.data.included || []);
          }
        });

        const processed = processListings(allData, allIncluded);
        const catalogFiltered = filterListingsByCatalogMode(processed, catalogMode);
        const conditionFiltered =
          catalogMode === SERVICES_CATALOG_MODE
            ? catalogFiltered
            : filterListingsByCondition(catalogFiltered, searchParams.pub_condition);
        const deliveryFiltered =
          catalogMode === SERVICES_CATALOG_MODE
            ? conditionFiltered
            : filterListingsByDeliveryOption(conditionFiltered, searchParams.pub_deliveryOption);
        const channelFiltered =
          catalogMode === SERVICES_CATALOG_MODE
            ? deliveryFiltered
            : filterListingsByChannel(deliveryFiltered, searchParams.channel);
        const contextualBrandValues =
          catalogMode === SERVICES_CATALOG_MODE ? null : getAvailableBrandValues(channelFiltered);
        const brandFiltered =
          catalogMode === SERVICES_CATALOG_MODE
            ? channelFiltered
            : filterListingsByBrand(channelFiltered, searchParams.pub_brand);

        // Client-side sort — all pages are already fetched so this is reliable
        const sorted = [...brandFiltered];
        if (sortValue === 'newest' || !sortValue) {
          sorted.sort(
            (a, b) => new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt)
          );
        } else if (sortValue === 'price-low') {
          sorted.sort(
            (a, b) => (a.attributes.price?.amount || 0) - (b.attributes.price?.amount || 0)
          );
        } else if (sortValue === 'price-high') {
          sorted.sort(
            (a, b) => (b.attributes.price?.amount || 0) - (a.attributes.price?.amount || 0)
          );
        }
        // 'relevance' keeps API order (pub_rank)
        const toShow = sortValue === 'relevance' ? brandFiltered : sorted;

        setAvailableBrandValues(contextualBrandValues);
        setSearchResults(toShow);
        return response;
      });
    })
    .catch(e => {
      console.error('Error fetching search results:', e);
      setAvailableBrandValues(null);
      setSearchResults([]);
    });
};
const fetchStaffPicks = (
  searchParams,
  setSearchResults,
  setAvailableBrandValues,
  setGeneratedAt,
  limit = null
) => () =>
  fetchStaffPicksApi({ ...searchParams, ...(limit ? { limit } : {}) })
    .then(response => {
      const data = response?.data || [];
      const meta = response?.meta || {};
      setSearchResults(data);
      if (setAvailableBrandValues) {
        setAvailableBrandValues(meta.availableBrandValues || getAvailableBrandValues(data));
      }
      if (setGeneratedAt) setGeneratedAt(meta.generatedAt || null);
      return response;
    })
    .catch(error => {
      console.error('Error fetching Staff Picks:', error);
      setSearchResults([]);
      if (setAvailableBrandValues) setAvailableBrandValues(null);
      return null;
    });

const mapDispatchToProps = dispatch => ({
  handleDomLoaded: () => dispatch(setDOMLoaded()),
  handleFirstLoaded: () => dispatch(setFirstLoaded()),
  handleStores: () => dispatch(fetchStoresWithReviews()),
  onFetchSearchResults: (
    searchParams,
    setSearchResults,
    setAvailableBrandValues,
    sortValue,
    catalogMode
  ) =>
    dispatch(
      fetchSearchResults(
        searchParams,
        setSearchResults,
        setAvailableBrandValues,
        sortValue,
        catalogMode
      )
    ),
  onFetchStaffPicks: (searchParams, setSearchResults, setAvailableBrandValues, setGeneratedAt) =>
    dispatch(
      fetchStaffPicks(searchParams, setSearchResults, setAvailableBrandValues, setGeneratedAt)
    ),
  onFetchHomepageStaffPicks: (setListings, setGeneratedAt) =>
    dispatch(fetchStaffPicks({}, setListings, null, setGeneratedAt, 5)),
});

const LandingPageCustom = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(LandingPageCustomComponent);

export default LandingPageCustom;
